import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

import {
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover as MuiPopover,
  Tooltip,
  Typography,
} from "@mui/material";
import { Bell } from "react-feather";

import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import useAuth from "../../hooks/useAuth";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  AppNotification,
} from "../../redux/slices/notifications";

const Popover = styled(MuiPopover)`
  .MuiPaper-root {
    width: 340px;
    ${(props) => props.theme.shadows[1]};
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
`;

const Indicator = styled(Badge)`
  .MuiBadge-badge {
    background: ${(props) => props.theme.header.indicator.background};
    color: ${(props) => props.theme.palette.common.white};
  }
`;

const NotificationHeader = styled(Box)`
  text-align: center;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const POLL_MS = 60_000;

function NavbarNotificationsDropdown() {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { items, unreadCount } = useAppSelector((s) => s.notifications);

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(fetchNotifications());
    const timer = setInterval(() => dispatch(fetchNotifications()), POLL_MS);
    return () => clearInterval(timer);
  }, [dispatch, isAuthenticated]);

  const handleClick = (notification: AppNotification) => {
    setOpen(false);
    if (!notification.readAt) {
      dispatch(markNotificationRead(notification.id));
    }
    if (notification.linkPath) {
      navigate(notification.linkPath);
    }
  };

  return (
    <React.Fragment>
      <Tooltip title="Ilmoitukset">
        <IconButton
          color="inherit"
          ref={ref}
          onClick={() => setOpen(true)}
          size="large"
        >
          <Indicator badgeContent={unreadCount}>
            <Bell />
          </Indicator>
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        open={isOpen}
      >
        <NotificationHeader p={2}>
          <Typography variant="subtitle1" color="textPrimary">
            {unreadCount > 0
              ? `${unreadCount} uutta ilmoitusta`
              : "Ei uusia ilmoituksia"}
          </Typography>
        </NotificationHeader>
        {items.length > 0 && (
          <React.Fragment>
            <List disablePadding sx={{ maxHeight: 360, overflowY: "auto" }}>
              {items.map((n) => (
                <ListItemButton
                  key={n.id}
                  divider
                  onClick={() => handleClick(n)}
                  sx={{ opacity: n.readAt ? 0.6 : 1 }}
                >
                  <ListItemText
                    primary={n.title}
                    primaryTypographyProps={{
                      variant: "subtitle2",
                      color: "textPrimary",
                      fontWeight: n.readAt ? "normal" : "bold",
                    }}
                    secondary={
                      (n.body ? `${n.body} — ` : "") +
                      format(new Date(n.createdAt), "d.M.yyyy HH:mm")
                    }
                  />
                </ListItemButton>
              ))}
            </List>
            {unreadCount > 0 && (
              <Box p={1} display="flex" justifyContent="center">
                <Button
                  size="small"
                  onClick={() => dispatch(markAllNotificationsRead())}
                >
                  Merkitse kaikki luetuiksi
                </Button>
              </Box>
            )}
          </React.Fragment>
        )}
      </Popover>
    </React.Fragment>
  );
}

export default NavbarNotificationsDropdown;
